import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * App-level error boundary: a render error anywhere below this point shows a
 * recoverable fallback instead of unmounting the whole app to a blank screen.
 * (The web app previously had none, so any uncaught render throw white-screened.)
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="min-h-screen grid place-items-center bg-white dark:bg-zinc-950 px-6 text-center">
        <div className="max-w-md">
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Something went wrong</h1>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
            An unexpected error occurred. Try reloading the page — if it keeps happening, contact support.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white dark:bg-zinc-100 dark:text-zinc-900"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
